import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  BarChart2,
  CalendarDays,
  Settings,
  LogOut,
  Globe,
  Calculator,
  Trophy,
  Menu,
  X,
  ChevronRight,
  Crown,
  FolderKanban,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { Logo } from "./Logo";
import { useAdmin } from "../hooks/useAdmin";
import { cn } from "@/lib/utils";

// ... imports and previous code ...

interface SidebarProps {
  activeTab: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  onOpenSettings: () => void;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  onNavigate,
  onLogout,
  onOpenSettings,
  isExpanded: controlledExpanded,
  onExpandedChange,
}) => {
  // Mobile Drawer State
  const [isOpen, setIsOpen] = useState(false);

  // Internal Desktop State (if not controlled)
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Derived State
  const isExpanded =
    controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const setExpanded = (value: boolean) => {
    if (onExpandedChange) {
      onExpandedChange(value);
    }
    if (controlledExpanded === undefined) {
      setInternalExpanded(value);
    }
  };

  const { t, language, setLanguage, isRTL } = useLanguage();
  const { isAdmin } = useAdmin();

  const menuItems = [
    {
      id: "dashboard",
      label: t.menuDashboard,
      icon: LayoutDashboard,
      path: "/dashboard",
    },
    {
      id: "ai-planner",
      label: "AI Study Planner",
      icon: CalendarDays,
      path: "/ai-planner",
    },
    {
      id: "rewards",
      label: t.menuRewards || "Rewards",
      icon: Trophy,
      path: "/dashboard?tab=rewards",
    },

    { id: "grades", label: t.menuGrades, icon: Calculator, path: "/grades" },
    {
      id: "analytics",
      label: t.menuAnalytics,
      icon: BarChart2,
      path: "/analytics",
    },
    {
      id: "projects",
      label: "Projects",
      icon: FolderKanban,
      path: "/projects",
    },
    {
      id: "leaderboards",
      label: t.menuLeaderboards,
      icon: Trophy,
      path: "/leaderboards",
    },

    ...(isAdmin
      ? [
          {
            id: "admin",
            label: "Admin",
            icon: Crown,
            path: "/dashboard?tab=admin",
          },
        ]
      : []),
  ];

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "ar" : "en");
  };

  const isItemActive = (item: (typeof menuItems)[0]) => {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;

    if (item.path.includes("?")) {
      const [path, search] = item.path.split("?");
      return currentPath === path && currentSearch.includes(search);
    } else {
      return (
        currentPath === item.path &&
        (!currentPath.includes("dashboard") || currentSearch === "")
      );
    }
  };

  // Desktop Sidebar - COLLAPSIBLE with OVERLAY
  const renderDesktopSidebar = () => (
    <motion.aside
      className={cn(
        "hidden lg:flex flex-col fixed top-0 h-screen bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-slate-100 dark:border-white/5 shadow-xl z-50 overflow-hidden",
        isRTL ? "right-0 border-l" : "left-0 border-r",
      )}
      initial={false}
      animate={{
        width: isExpanded ? 256 : 72,
      }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-slate-100 dark:border-white/5 min-h-[72px]">
        <Logo
          className="h-8 w-auto flex-shrink-0"
          textClassName="text-lg font-bold text-slate-800 dark:text-white"
          showText={isExpanded}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden custom-scrollbar">
        <div className="space-y-1 px-2">
          {menuItems.map((item) => {
            const isActive = isItemActive(item);
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.path)}
                title={!isExpanded ? item.label : undefined}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group",
                  isActive
                    ? "bg-yellow-50 text-yellow-900 font-semibold shadow-sm border-l-2 border-l-yellow-500 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-l-yellow-400"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-white",
                )}
              >
                <item.icon
                  size={20}
                  className={cn(
                    "flex-shrink-0 transition-colors",
                    isActive
                      ? "text-yellow-500 dark:text-yellow-400"
                      : "text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300",
                  )}
                />
                <AnimatePresence>
                  {isExpanded && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15 }}
                      className="whitespace-nowrap overflow-hidden text-sm"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
                {isActive && isExpanded && (
                  <ChevronRight
                    size={14}
                    className={cn(
                      "opacity-50 ml-auto flex-shrink-0",
                      isRTL && "rotate-180",
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Bottom Actions */}
      <div className="mt-auto border-t border-slate-100 dark:border-white/5 p-2 space-y-1">
        <button
          onClick={toggleLanguage}
          title={
            !isExpanded
              ? language === "en"
                ? "العربية"
                : "English"
              : undefined
          }
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <Globe size={18} className="flex-shrink-0" />
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
                className="text-sm whitespace-nowrap overflow-hidden"
              >
                {language === "en" ? "العربية" : "English"}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
        <button
          onClick={onOpenSettings}
          title={!isExpanded ? t.menuSettings : undefined}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <Settings size={18} className="flex-shrink-0" />
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
                className="text-sm whitespace-nowrap overflow-hidden"
              >
                {t.menuSettings}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
        <button
          onClick={onLogout}
          title={!isExpanded ? t.logout : undefined}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
        >
          <LogOut size={18} className="flex-shrink-0" />
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
                className="text-sm whitespace-nowrap overflow-hidden"
              >
                {t.logout}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );

  // Mobile Sidebar - ANIMATED DRAWER
  const renderMobileSidebar = () => (
    <>
      {/* Mobile Trigger */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "lg:hidden fixed top-4 z-40 p-2.5 rounded-xl bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-700",
          isRTL ? "right-4" : "left-4",
        )}
      >
        <Menu size={20} className="text-slate-600 dark:text-slate-300" />
      </button>

      {/* Mobile Drawer Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 dark:bg-black/50 backdrop-blur-sm z-[60] lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            initial={{ x: isRTL ? "100%" : "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: isRTL ? "100%" : "-100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={cn(
              "fixed top-0 z-[70] h-screen w-72 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-slate-100 dark:border-white/5 shadow-2xl",
              isRTL ? "right-0 border-l" : "left-0 border-r",
            )}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X size={20} className="text-slate-600 dark:text-slate-400" />
            </button>

            {/* Content */}
            <div className="flex flex-col h-full p-4 pt-16">
              {/* Logo */}
              <div className="flex items-center gap-3 px-2 mb-6">
                <Logo
                  className="h-10 w-auto"
                  textClassName="text-xl font-bold text-slate-800 dark:text-white"
                />
              </div>

              {/* Navigation */}
              <nav className="flex-1 space-y-1 overflow-y-auto custom-scrollbar">
                {menuItems.map((item) => {
                  const isActive = isItemActive(item);
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        onNavigate(item.path);
                        setIsOpen(false);
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group",
                        isActive
                          ? "bg-gradient-to-r from-yellow-100 to-amber-50 dark:from-yellow-500/10 dark:to-amber-500/5 text-yellow-900 dark:text-yellow-400 font-semibold shadow-sm"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white",
                      )}
                    >
                      <item.icon
                        size={20}
                        className={
                          isActive
                            ? "text-yellow-500 dark:text-yellow-400"
                            : "text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300"
                        }
                      />
                      <span className="text-sm">{item.label}</span>
                      {isActive && (
                        <ChevronRight
                          size={14}
                          className={cn(
                            "opacity-50 ml-auto",
                            isRTL && "rotate-180",
                          )}
                        />
                      )}
                    </button>
                  );
                })}
              </nav>

              {/* Bottom Actions */}
              <div className="mt-auto space-y-1 pt-4 border-t border-slate-100 dark:border-white/5">
                <button
                  onClick={() => {
                    toggleLanguage();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  <Globe size={18} />
                  <span className="text-sm">
                    {language === "en" ? "العربية" : "English"}
                  </span>
                </button>
                <button
                  onClick={() => {
                    onOpenSettings();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  <Settings size={18} />
                  <span className="text-sm">{t.menuSettings}</span>
                </button>
                <button
                  onClick={() => {
                    onLogout();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                >
                  <LogOut size={18} />
                  <span className="text-sm">{t.logout}</span>
                </button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <>
      {renderDesktopSidebar()}
      {renderMobileSidebar()}
    </>
  );
};
